import React, { MutableRefObject } from "react";
import { Animated } from "react-native";
import {
  DraggingComponent,
  DropZone,
  Point,
} from "../../../utilities/drag-drop";
import { Registry } from "../../../utilities/layout/registry";
import { Vector2D } from "../../../utilities/vector";
import { DEFAULT_VALUE_XY } from "./constants";

type UseHandleDragArgs = {
  draggingComponentRef: MutableRefObject<DraggingComponent | null>;
  animationRegistryRef: MutableRefObject<Registry<Animated.ValueXY>>;
  offsetRef: React.MutableRefObject<Point | null>;
  onDragStart?: (
    draggingComponent: DraggingComponent,
    hitZones?: DropZone[]
  ) => void;
};

export const useDrag = ({
  draggingComponentRef,
  animationRegistryRef,
  offsetRef,
  onDragStart,
}: UseHandleDragArgs) => {
  function onDrag(ref: any, children: any, data: any) {
    if (!ref) {
      return;
    }

    ref.measure((...args: any[]) => {
      let newLocation = new Animated.ValueXY(DEFAULT_VALUE_XY, {
        useNativeDriver: true,
      });

      const tileOffset = Vector2D.create({
        x: args[4],
        y: args[5],
      });

      const newDraggingComponent = {
        ref,
        data,
        children: children,
        startPosition: {
          x: tileOffset.x,
          y: tileOffset.y,
          width: args[2],
          height: args[3],
        },
      };

      const hasAnimation = animationRegistryRef.current.has(data.config.id);

      if (hasAnimation) {
        animationRegistryRef.current.set(data.config.id, newLocation);
      } else {
        animationRegistryRef.current.register(data.config.id, newLocation);
      }

      const newOffset = tileOffset.toComponents();

      newLocation.setOffset(newOffset);

      offsetRef.current = newOffset;

      draggingComponentRef.current = newDraggingComponent;

      if (onDragStart) {
        onDragStart(newDraggingComponent);
      }
    });
  }

  const clearDragContext = () => {
    draggingComponentRef.current = null;
    offsetRef.current = null;
  };

  return {
    onDrag,
    clearDragContext,
  };
};
